import { createStore, type StoreApi } from "zustand/vanilla";

import type { AudioChannel } from "./catalog";

export interface AudioPreferences {
  readonly muted: boolean;
  readonly masterVolume: number;
  readonly channelVolumes: Readonly<Record<AudioChannel, number>>;
}

export type AudioPreferencesInput = Partial<
  Omit<AudioPreferences, "channelVolumes">
> & {
  readonly channelVolumes?: Partial<Record<AudioChannel, number>>;
};

export type AudioPreferencesStore = StoreApi<AudioPreferences>;

export const DEFAULT_AUDIO_PREFERENCES: AudioPreferences = Object.freeze({
  muted: false,
  masterVolume: 1,
  channelVolumes: Object.freeze({
    music: 0.65,
    voice: 1,
    sfx: 0.9
  })
});

export function clampVolume(value: number): number {
  if (!Number.isFinite(value)) {
    return 0;
  }

  return Math.min(1, Math.max(0, value));
}

export function normalizeAudioPreferences(
  input: AudioPreferencesInput = {}
): AudioPreferences {
  return {
    muted: input.muted ?? DEFAULT_AUDIO_PREFERENCES.muted,
    masterVolume: clampVolume(
      input.masterVolume ?? DEFAULT_AUDIO_PREFERENCES.masterVolume
    ),
    channelVolumes: {
      music: clampVolume(
        input.channelVolumes?.music ??
          DEFAULT_AUDIO_PREFERENCES.channelVolumes.music
      ),
      voice: clampVolume(
        input.channelVolumes?.voice ??
          DEFAULT_AUDIO_PREFERENCES.channelVolumes.voice
      ),
      sfx: clampVolume(
        input.channelVolumes?.sfx ?? DEFAULT_AUDIO_PREFERENCES.channelVolumes.sfx
      )
    }
  };
}

export function createAudioPreferencesStore(
  initialPreferences: AudioPreferencesInput = {}
): AudioPreferencesStore {
  const initialState = normalizeAudioPreferences(initialPreferences);
  return createStore<AudioPreferences>(() => initialState);
}

export function updateAudioPreferences(
  store: AudioPreferencesStore,
  patch: AudioPreferencesInput
): AudioPreferences {
  const current = store.getState();
  const next = normalizeAudioPreferences({
    ...current,
    ...patch,
    channelVolumes: {
      ...current.channelVolumes,
      ...patch.channelVolumes
    }
  });

  store.setState(next, true);
  return next;
}
